'use client';

import React from 'react';
import { urlFor } from '@/sanity/lib/image';
import AffiliateLink from '@/components/common/AffiliateLink';
import { decorateBookingUrl } from '@/lib/affiliates/utils/booking';
import { decorateTripUrl, getLocaleFromLanguage, DEFAULT_TRIP_AFFILIATE_PARAMS } from '@/lib/affiliates/utils/trip';
import { t } from '@/i18n/translations';

/**
 * Карточка отеля в стиле Gallery·Field (`.ho`). Используется в секции
 * «Проверенные отели» на странице города и на странице всех отелей города.
 * Данные — документ типа `hotel` (см. src/sanity/queries/city.js).
 */

export const PRICE_SYMBOL = { budget: '€', mid: '€€', upper: '€€€', luxury: '€€€€' };

// Порядок для бейджей на карточке: сначала отличающие / премиальные удобства.
// Завтрак НЕ показываем чипом — он есть почти у всех и уже звучит в описании;
// бейджи должны выделять отель, а не повторять очевидное.
// Подписи удобств — в i18n-неймспейсе accommodationUi.amenities (по тем же ключам).
export const AMENITY_ORDER = ['pool', 'spa', 'seaFront', 'beach', 'parkingFree', 'fitness', 'pets', 'ruStaff', 'family', 'parking'];

// Карточка: максимум 3 самых характерных удобства — чтобы не захламлять.
export function amenitiesFor(hotel, lang = 'ru', max = 3) {
    const isRu = lang === 'ru';
    const labels = t(lang, 'accommodationUi').amenities;
    const out = [];
    for (const key of AMENITY_ORDER) {
        if (out.length >= max) break;
        // «Беспл. парковка» уже покрывает «Парковку» — не дублируем
        if (key === 'parking' && hotel.parkingFree) continue;
        // «Русскоговорящий персонал» — релевантно только русской версии
        if (key === 'ruStaff' && !isRu) continue;
        if (hotel[key]) out.push(labels[key]);
    }
    return out;
}

export const fmtKm = (km, isRu) => (isRu ? String(km).replace('.', ',') : String(km));

/**
 * Декорированные партнёрские ссылки для отеля. Вынесено, чтобы переиспользовать
 * (карточка + попап на карте).
 */
export function hotelLinks(h, { lang, citySlug }) {
    const sub1 = `${citySlug}-hotel-${h.slug || h._id}`;
    return {
        bookingHref: h.bookingUrl ? decorateBookingUrl(h.bookingUrl, { subId: sub1 }, lang) : null,
        tripHref: h.tripUrl
            ? decorateTripUrl(h.tripUrl, { ...DEFAULT_TRIP_AFFILIATE_PARAMS, locale: getLocaleFromLanguage(lang), sub1 }, lang)
            : null,
    };
}

export default function HotelCard({ hotel: h, lang = 'ru', countrySlug, citySlug, position = 'city-hotel' }) {
    const isRu = lang === 'ru';
    const ui = t(lang, 'accommodationUi');

    const img = h.cover?.image
        ? urlFor(h.cover.image).width(800).height(560).fit('crop').auto('format').url()
        : null;
    const alt = h.cover?.alt || h.name;
    const area = isRu ? h.area?.ru : h.area?.en;
    const desc = isRu ? h.shortDescriptionRu : h.shortDescriptionEn;
    const amenities = amenitiesFor(h, lang);
    const stars = Number(h.stars) || 0;

    const { bookingHref, tripHref } = hotelLinks(h, { lang, citySlug });

    const locParts = [];
    if (area) locParts.push(area);
    if (typeof h.distCenterKm === 'number') {
        locParts.push(ui.card.toCentre(fmtKm(h.distCenterKm, isRu)));
    }

    return (
        <article className="ho">
            <div className="ho-ph">
                {img && <img src={img} alt={alt} loading="lazy" />}
                {typeof h.reviewScore === 'number' && (
                    <span className="ho-score" title={h.reviewCount ? `${h.reviewCount} ${ui.card.reviews(h.reviewCount)}` : undefined}>
                        {h.reviewScore.toFixed(1)}
                    </span>
                )}
                <span className="ho-badges">
                    {stars > 0 && <span className="ho-stars" aria-label={`${stars} ${ui.card.stars(stars)}`}>{'★'.repeat(stars)}</span>}
                    {PRICE_SYMBOL[h.priceTier] && <span className="ho-price">{PRICE_SYMBOL[h.priceTier]}</span>}
                </span>
            </div>

            <div className="ho-b">
                <h3>{h.name}</h3>
                {locParts.length > 0 && <span className="ho-loc">{locParts.join(' · ')}</span>}
                {desc && <p className="ho-ex">{desc}</p>}
                {/* Бейджи + кнопки — единый блок, прижатый к низу карточки
                    (бейджи привязаны к кнопкам под ними, а не к тексту над ними) */}
                {(amenities.length > 0 || bookingHref || tripHref) && (
                <div className="ho-bottom">
                    {amenities.length > 0 && (
                        <ul className="ho-am">
                            {amenities.map((a) => <li key={a}>{a}</li>)}
                        </ul>
                    )}
                    {(bookingHref || tripHref) && (
                        <div className="ho-cta">
                            {bookingHref ? (
                                <AffiliateLink href={bookingHref} provider="booking" vertical="stay" country={countrySlug} position={position} className="ho-btn">
                                    {ui.card.checkBooking}
                                </AffiliateLink>
                            ) : tripHref ? (
                                <AffiliateLink href={tripHref} provider="trip" vertical="stay" country={countrySlug} position={position} className="ho-btn">
                                    {ui.card.checkTrip}
                                </AffiliateLink>
                            ) : null}
                        </div>
                    )}
                </div>
                )}
            </div>
        </article>
    );
}
